import React, { useId, useLayoutEffect, useRef, useState } from 'react';

/** Stable ids so a screen can label its panel: <div {...tabPanelProps(base, tab)}>. */
export const tabId = (base, id) => `${base}-tab-${String(id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
export const panelId = (base, id) => `${base}-panel-${String(id).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
export const tabPanelProps = (base, id) => ({ role: 'tabpanel', id: panelId(base, id), 'aria-labelledby': tabId(base, id), tabIndex: 0 });

/**
 * <TabBar /> — underline tabs (Job detail, Property record, Billing, Jobs saved-views).
 *
 * @param {{id, label, count?, disabled?}[]} tabs  count renders as a pill (or plain with `plainCount`)
 * @param {string} active
 * @param {(id)=>void} onChange
 * @param {string} [ariaLabel]   names the tab list ("Job sections")
 * @param {string} [idBase]      prefix for tab/panel ids — pass the same value to tabPanelProps()
 *                               so the panel is labelled by its tab (aria-controls is only
 *                               emitted when idBase is given, since the panel must exist)
 *
 * Keyboard: ←/→ move between tabs and activate them, Home/End jump to the ends.
 * For URL-synced tabs use `useTabParam()` from hooks/useUrlState.
 */
const TabBar = ({ tabs = [], active, onChange, plainCount = false, style, ariaLabel, idBase, className = '' }) => {
  const autoBase = `cw-tabs-${useId().replace(/:/g, '')}`;
  const base = idBase || autoBase;
  const refs = useRef({});
  const enabled = tabs.filter((t) => !t.disabled);
  const activeExists = tabs.some((t) => t.id === active);
  // One underline glides to the selected tab (placed without a glide on first paint).
  const [bar, setBar] = useState(null);
  const ready = useRef(false);
  const tabKey = tabs.map((t) => t.id).join('|');
  useLayoutEffect(() => {
    const el = refs.current[active];
    setBar(el ? { x: el.offsetLeft, w: el.offsetWidth, glide: ready.current } : null);
    if (el) ready.current = true;
  }, [active, tabKey]);

  const move = (fromId, delta) => {
    if (!enabled.length) return;
    const i = enabled.findIndex((t) => t.id === fromId);
    let next;
    if (delta === 'first') next = enabled[0];
    else if (delta === 'last') next = enabled[enabled.length - 1];
    else next = enabled[(i + delta + enabled.length) % enabled.length];
    if (!next) return;
    if (onChange) onChange(next.id);
    const el = refs.current[next.id];
    if (el) { el.focus(); el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }); }
  };

  const onKeyDown = (t) => (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); move(t.id, 1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); move(t.id, -1); }
    else if (e.key === 'Home') { e.preventDefault(); move(t.id, 'first'); }
    else if (e.key === 'End') { e.preventDefault(); move(t.id, 'last'); }
  };

  return (
    <div className={`cw-tabs cw-scroll${bar ? ' has-bar' : ''} ${className}`.trim()} style={style} role="tablist" aria-label={ariaLabel}>
      {tabs.map((t, idx) => {
        const selected = active === t.id;
        const focusable = selected || (!activeExists && idx === 0);
        const hasCount = t.count != null && t.count !== '';
        return (
          <button
            key={t.id}
            ref={(el) => { refs.current[t.id] = el; }}
            type="button"
            role="tab"
            id={tabId(base, t.id)}
            aria-selected={selected}
            aria-controls={idBase ? panelId(base, t.id) : undefined}
            tabIndex={focusable ? 0 : -1}
            disabled={t.disabled}
            className={`cw-tab${selected ? ' active' : ''}`}
            onClick={() => onChange && onChange(t.id)}
            onKeyDown={onKeyDown(t)}
          >
            {t.label}
            {hasCount && (
              <span className={plainCount ? 'count-plain' : 'count'}>
                {t.count}
              </span>
            )}
          </button>
        );
      })}
      {bar && <span className={`cw-tabs-bar${bar.glide ? ' glide' : ''}`} aria-hidden="true" style={{ transform: `translateX(${bar.x}px)`, width: bar.w }} />}
    </div>
  );
};

export default TabBar;
